import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { SectionHeader } from "@/components/sections/Services";
import { HOME } from "@/constants/testIds";

const testimonials = [
  {
    role: "Head of Revenue",
    company: "A leading digital publishing network",
    quote:
      "Our eCPMs jumped 38% within the first quarter on MonetiseUP. The dedicated account team optimized everything from header bidding to brand safety — it just works.",
    rating: 5,
  },
  {
    role: "Programmatic Lead",
    company: "A global media & entertainment brand",
    quote:
      "The targeting precision is unreal. We launched a global campaign across 14 markets and saw a +35% brand lift with conversion rates beating our benchmarks.",
    rating: 5,
  },
  {
    role: "Founder",
    company: "A Tier-1 manga streaming platform",
    quote:
      "Reader-first ad placements were a game-changer. Revenue per chapter doubled while retention actually improved. The manga vertical is finally taken seriously.",
    rating: 5,
  },
  {
    role: "VP, Growth",
    company: "A multi-property content publisher",
    quote:
      "Real-time analytics, transparent reporting, and a 24/7 team that actually responds. MonetiseUP is the most reliable ad-tech partner we have ever worked with.",
    rating: 5,
  },
];

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const t = testimonials[idx];

  useEffect(() => {
    const id = setInterval(
      () => setIdx((i) => (i + 1) % testimonials.length),
      6000,
    );
    return () => clearInterval(id);
  }, []);

  const next = () => setIdx((i) => (i + 1) % testimonials.length);
  const prev = () =>
    setIdx((i) => (i - 1 + testimonials.length) % testimonials.length);

  return (
    <section
      data-testid={HOME.testimonialsSection}
      className="relative py-32"
    >
      <div className="container-x">
        <SectionHeader
          align="center"
          eyebrow="Voices"
          title={
            <>
              Loved by publishers
              <br />
              <span className="aurora-text">and advertisers alike</span>
            </>
          }
        />

        <div className="relative max-w-4xl mx-auto">
          <div className="absolute -top-6 -left-4 text-[#00F0FF]/20">
            <Quote className="w-24 h-24" />
          </div>

          <div className="relative glass-strong rounded-3xl p-10 md:p-14 min-h-[320px] flex flex-col justify-between">
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex gap-1 mb-6">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-[#00F0FF] text-[#00F0FF]"
                    />
                  ))}
                </div>
                <p className="font-display text-2xl md:text-3xl leading-snug mb-8">
                  “{t.quote}”
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0047FF] to-[#00F0FF] grid place-items-center border-2 border-[#00F0FF]/40">
                    <span className="font-display text-lg font-bold text-white">
                      {t.role.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-white/90">{t.role}</div>
                    <div className="text-sm text-white/55">at {t.company}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center justify-between mt-10 pt-6 border-t border-white/5">
              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`Show testimonial ${i + 1}`}
                    data-testid={`testimonial-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all ${
                      i === idx
                        ? "w-8 bg-[#00F0FF]"
                        : "w-1.5 bg-white/20 hover:bg-white/40"
                    }`}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={prev}
                  data-testid="testimonial-prev"
                  className="w-10 h-10 rounded-full border border-white/10 hover:border-[#00F0FF]/50 grid place-items-center transition-colors"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={next}
                  data-testid="testimonial-next"
                  className="w-10 h-10 rounded-full border border-white/10 hover:border-[#00F0FF]/50 grid place-items-center transition-colors"
                  aria-label="Next testimonial"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
